import { Component, Inject, Prop } from 'vue-property-decorator';
import { Solicitud } from '@/shared/model/solicitud.model';
import { Solucion } from '@/shared/model/solucion.model';
import { IDefinicionEvaluacion } from '@/shared/model/definicion-evaluacion.model';
import { mixins } from 'vue-class-component';
import EvaluacionSnpMixin from '@/entities/snp/evaluacion/evaluacion-snp-mixin';
import { IRevision } from '@/shared/model/revision.model';
import { CuestionarioSnp, PreguntaCuestionarioSnp } from '@/shared/model/snp/cuestionario.model';
import { EstadoRevision } from '@/shared/model/enumerations/estado-revision.model';
import CuestionarioSnpComponent from '@/entities/snp/snippets/cuestionario/cuestionario-snp.vue';
import SolicitudService from '@/entities/solicitud/solicitud.service';
import FirmarDictamenSnpComponent from '@/entities/snp/evaluador/dictamen/firmar-dictamen-snp.vue';
import { TipoComision } from '@/shared/model/enumerations/tipo-comision.model';
import { EstadoInsaculacion } from '@conacyt/insaculacion-client';

@Component({
  computed: {
    numeroCvuEnSesion: {
      get() {
        return this.$store.getters.userInfo?.cvu ?? '';
      },
    },
    loginEnSesion: {
      get() {
        return this.$store.getters.account?.login ?? '';
      },
    },
  },
  components: {
    'cuestionario-snp': CuestionarioSnpComponent,
    'firmar-dictamen-snp': FirmarDictamenSnpComponent,
  },
})
export default class ValoracionIndividualComponent extends mixins(EvaluacionSnpMixin) {
  @Inject('solicitudService') private readonly solicitudService: () => SolicitudService;

  public readonly numeroCvuEnSesion!: string;
  public readonly loginEnSesion!: string;

  @Prop({ default: false })
  public solicitud!: Solicitud;

  @Prop({ required: false })
  public solucion!: Solucion;

  public cuestionarioInicial: CuestionarioSnp = {};
  private iniciandoComonente = false;
  public preguntas: Array<PreguntaCuestionarioSnp> = [];
  public cuestionario: CuestionarioSnp = {};

  private buscandoDefinicionEvaluacion = false;
  private definicionEvaluacion: IDefinicionEvaluacion = {};
  private evaluacionId = '';
  public evaluacionDescripcion = '';
  public tipoComision: TipoComision = null;

  private obteniendoRevisionEvaluador = false;
  public revisionEvaluador: IRevision = {};

  public guardandoEvaluacion = false;
  public verModalGuardar = false;

  public finalizandoEvaluacion = false;
  public verModalFinalizar = false;

  public cancelandoEvaluacion = false;
  public verModalCancelar = false;

  public estadoInsaculacion: EstadoInsaculacion = null;

  /**
   * Carga inicial
   */
  mounted() {
    this.iniciarComponente();
  }

  private iniciarComponente(): void {
    this.iniciandoComonente = true;

    const modalidad = this.solicitud?.programa?.cveModalidad;
    const params = this.solucion?.params;

    const cuestionariosPorModalidad: Record<string, any> = {
      ESC: params?.cuestionario_esc,
      'ENS REM': params?.cuestionario_ens_rem,
    };

    const cuestionario = modalidad ? cuestionariosPorModalidad[modalidad] : null;

    if (cuestionario?.preguntas?.length) {
      this.preguntas = cuestionario.preguntas.map(PreguntaCuestionarioSnp.fromObject);
    } else {
      this.alertService().showWarning(this, this.$t('apeironGwApp.valoracionIndividualSNP.alerts.noPreguntasEncontradas').toString());
    }

    this.revisionEvaluador = {};
    this.definicionEvaluacion = {};
    this.evaluacionId = '';
    this.evaluacionDescripcion = '';

    this.obtenerEvaluacionDeSolucion();
    this.iniciandoComonente = false;
  }

  /**
   * Obtiene la definición de evaluación.
   * Ejemplo: evaluación individual dictaminadora
   */
  public obtenerEvaluacionDeSolucion(): void {
    this.buscandoDefinicionEvaluacion = true;
    this.solucionService()
      .findEvaluacionDefinicion(this.solucion?.id ?? '')
      .then(definicionEvaluacion => {
        this.definicionEvaluacion = definicionEvaluacion;

        for (const definicion of this.definicionEvaluacion.revisiones) {
          if (definicion.id == this.$route.params.evaluacionId) {
            this.evaluacionId = definicion.id;
            this.evaluacionDescripcion = definicion.descripcion;
            this.tipoComision = definicion.tipoComision;

            this.obtenerEstadoDeInsaculacionOError(this.tipoComision, this.solucion)
              .then(estadoInsaculacion => (this.estadoInsaculacion = estadoInsaculacion))
              .catch(error => {
                this.alertService().showHttpError(this, error.response);
              });
          }
        }
        if (!this.evaluacionId) {
          this.alertService().showError(this, this.$t('apeironGwApp.valoracionIndividualSNP.alerts.sinEvaluacionDefinida').toString());
        } else {
          this.obtenerRevisionEvaluador();
        }
      })
      .catch(() => {
        this.alertService().showError(this, this.$t('apeironGwApp.valoracionIndividualSNP.alerts.sinEvaluacionDefinida').toString());
        this.definicionEvaluacion = {};
      })
      .finally(() => {
        this.buscandoDefinicionEvaluacion = false;
      });
  }

  /**
   * Obtiene las respuestas de la evaluación del evaluador.
   */
  public obtenerRevisionEvaluador(): void {
    this.obteniendoRevisionEvaluador = true;
    this.revisionService()
      .findByEvaluacionIdAndRevisorIdAndSolicitudId(this.evaluacionId, this.numeroCvuEnSesion, this.solicitud.id)
      .then(response => {
        this.revisionEvaluador = response;
        this.cuestionarioInicial = CuestionarioSnp.fromObject(this.revisionEvaluador.respuesta?.resultados ?? new CuestionarioSnp());
      })
      .catch(err => {
        if (err.response?.status != 404) {
          this.alertService().showError(
            this,
            err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionIndividualSNP.alerts.ocurrioUnError').toString()
          );
        }
      })
      .finally(() => {
        this.obteniendoRevisionEvaluador = false;
      });
  }

  public manejaFormularioCambia(cuestionario: CuestionarioSnp): void {
    this.cuestionario = cuestionario;
  }

  public mostrarModalGuardar(): void {
    this.verModalGuardar = true;
  }

  /**
   * Guarda las respuestas del cuestionario en el sistema.
   */
  public guardarEvaluacion(): void {
    if (EstadoInsaculacion.Aprobada != this.estadoInsaculacion || EstadoRevision.ASIGNADA != this.revisionEvaluador.estado) {
      return;
    }
    this.guardandoEvaluacion = true;
    this.verModalGuardar = false;
    this.revisionEvaluador.respuesta.resultados = this.cuestionario;
    this.revisionService()
      .update(this.revisionEvaluador)
      .then(() => {
        this.obtenerRevisionEvaluador();
        this.alertService().showSuccess(this, this.$t('apeironGwApp.valoracionIndividualSNP.alerts.evaluacionGuardada').toString());
      })
      .catch(err => {
        this.alertService().showError(
          this,
          err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionIndividualSNP.alerts.ocurrioUnError').toString()
        );
      })
      .finally(() => {
        this.guardandoEvaluacion = false;
      });
  }

  public mostrarModalFinalizar(): void {
    this.verModalFinalizar = true;
  }

  /**
   * Actualiza el estado de la evaluación a EVALUADA
   * Como acción de finalizar evaluación.
   */
  public finalizarEvaluacion(): void {
    if (EstadoInsaculacion.Aprobada != this.estadoInsaculacion || EstadoRevision.ASIGNADA != this.revisionEvaluador.estado) {
      return;
    }
    this.verModalFinalizar = false;
    if (this.puedeFinalizar) {
      this.finalizandoEvaluacion = true;
      this.revisionEvaluador.estado = EstadoRevision.REVISADA;
      this.revisionEvaluador.respuesta.resultados = this.cuestionario;
      this.revisionService()
        .update(this.revisionEvaluador)
        .then(() => {
          this.solicitudService()
            .find(this.revisionEvaluador.solicitudResumen.solicitudId)
            .then(res => {
              const revisor = (res.revisores ?? []).find(revisor => revisor.cvu == this.revisionEvaluador.revisor.revisorId);
              revisor.estado = EstadoRevision.REVISADA;
              this.solicitudService()
                .updateRevisores(res)
                .catch(err => {
                  this.alertService().showError(
                    this,
                    err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionIndividualSNP.alerts.ocurrioUnError').toString()
                  );
                });
            })
            .catch(err => {
              this.alertService().showError(
                this,
                err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionIndividualSNP.alerts.ocurrioUnError').toString()
              );
            });
          this.obtenerRevisionEvaluador();
          this.alertService().showSuccess(this, this.$t('apeironGwApp.valoracionIndividualSNP.alerts.evaluacionFinalizada').toString());
        })
        .catch(err => {
          this.alertService().showError(
            this,
            err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionIndividualSNP.alerts.ocurrioUnError').toString()
          );
        })
        .finally(() => {
          this.finalizandoEvaluacion = false;
        });
    }
  }

  public mostrarModalCancelar(): void {
    this.verModalCancelar = true;
  }

  /**
   * Actualiza el estado de la evaluación a CANCELADA
   * Como acción de cancelar la evaluación.
   */
  public cancelarEvaluacion(): void {
    if (EstadoInsaculacion.Aprobada != this.estadoInsaculacion || EstadoRevision.ASIGNADA != this.revisionEvaluador.estado) {
      return;
    }
    this.verModalCancelar = false;
    if (this.cuestionario?.comentariosGenerales) {
      this.cancelandoEvaluacion = true;
      this.revisionEvaluador.estado = EstadoRevision.CANCELADA;
      this.revisionService()
        .update(this.revisionEvaluador)
        .then(() => {
          this.solicitudService()
            .find(this.revisionEvaluador.solicitudResumen.solicitudId)
            .then(res => {
              const revisor = (res.revisores ?? []).find(
                revisor =>
                  revisor.cvu == this.revisionEvaluador.revisor.revisorId && this.revisionEvaluador.evaluacionId == revisor.evaluacionId
              );
              revisor.estado = EstadoRevision.CANCELADA;
              this.solicitudService()
                .updateRevisores(res)
                .catch(err => {
                  this.alertService().showError(
                    this,
                    err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionIndividualSNP.alerts.ocurrioUnError').toString()
                  );
                });
            })
            .catch(err => {
              this.alertService().showError(
                this,
                err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionIndividualSNP.alerts.ocurrioUnError').toString()
              );
            });
          this.obtenerRevisionEvaluador();
          this.alertService().showSuccess(this, this.$t('apeironGwApp.valoracionIndividualSNP.alerts.evaluacionCancelada').toString());
        })
        .catch(err => {
          this.alertService().showError(
            this,
            err?.response?.data?.detail ?? this.$t('apeironGwApp.valoracionIndividualSNP.alerts.ocurrioUnError').toString()
          );
        })
        .finally(() => {
          this.cancelandoEvaluacion = false;
        });
    }
  }

  get mostrarOverlay(): boolean {
    return (
      this.guardandoEvaluacion ||
      this.obteniendoRevisionEvaluador ||
      this.buscandoDefinicionEvaluacion ||
      this.iniciandoComonente ||
      this.finalizandoEvaluacion ||
      this.cancelandoEvaluacion
    );
  }

  get puedeFinalizar(): boolean {
    return this.cuestionario.estaCompleto;
  }
}
